import { useField } from 'formik';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { ColorPalettePicker } from '@treams/treams-ui/common/ColorPalettePicker';
import { tokens } from '@treams/treams-ui/theme/tokens';

import {
	type TalentReviewCellColors,
	customCellColor,
	encodeCellColor,
} from '../../talent-reviews/v2/talentReviewColors';
import TalentReviewLabel from './TalentReviewLabel';
import TalentReviewNorm from './TalentReviewNorm';

const Container = styled(Box)`
	margin-left: 10px;
	flex: 1;
	min-width: 0;
`;

const ColorPickerPosition = styled.div`
	position: absolute;
	top: 6px;
	left: 6px;
	z-index: 1;
`;

const CellToolbar = styled(Box)`
	position: absolute;
	top: 100%;
	transform: translateY(-50%);
	background: white;
`;

const CellToolbarUp = styled(Box)`
	height: 50%;
	width: 100%;
	background: ${tokens.surface.secondary};
	position: absolute;
`;

const CellContainer = styled(Flex)<{ $borderColor: string; $labelColor: string }>`
	flex-grow: 0;
	flex-shrink: 0;
	background: ${tokens.surface.secondary};
	width: 100%;
	min-height: 140px;
	border-radius: 6px;
	position: relative;
	color: ${(props) => props.$labelColor};

	align-items: center;
	justify-content: center;

	border: 2px solid ${(props) => props.$borderColor};
`;

const Instruction = styled(Flex)`
	margin-top: 20px;
	justify-content: center;

	color: ${tokens.text.secondary};
`;

export default function TalentReviewCell(props: { name: string; colors: TalentReviewCellColors }) {
	const { t } = useTranslation();
	const [{ value: hasNorm }] = useField<boolean>('hasNorm');
	const [{ value: color }, , { setValue: setColor }] = useField<number | undefined>(
		props.name + '.color',
	);

	const borderColor = props.colors.border;
	const labelColor = props.colors.label;
	const selectedColor = customCellColor(color);

	return (
		<Container>
			<CellContainer $borderColor={borderColor} $labelColor={labelColor}>
				<ColorPickerPosition>
					<ColorPalettePicker
						value={selectedColor}
						label={t('talentReviews.v2.template.pickColor')}
						onChange={(pickedColor) => {
							const nextColor = encodeCellColor(pickedColor);
							setColor(color === nextColor ? undefined : nextColor);
						}}
					/>
				</ColorPickerPosition>

				{hasNorm && <TalentReviewNorm name={props.name + '.norm'} hex={labelColor} />}

				<CellToolbar>
					<CellToolbarUp />
					<Box style={{ position: 'relative' }}>
						<TalentReviewLabel hex={labelColor} name={props.name + '.label'} />
					</Box>
				</CellToolbar>
			</CellContainer>
			<Instruction>
				<TalentReviewLabel
					multiline
					hex={tokens.text.secondary}
					name={props.name + '.instruction'}
				/>
			</Instruction>
		</Container>
	);
}
